import type { LocalCase, LocalProcedure } from '@iculogbook/shared';

/**
 * What to do with one record arriving from the server.
 *
 * This is the highest-risk function in the application. Everything else that
 * goes wrong is an inconvenience; getting this wrong silently destroys work a
 * trainee cannot reconstruct. It is therefore a pure function with every branch
 * of the matrix tested, rather than logic tangled into the puller.
 *
 * The rule, from ARCHITECTURE.md: **if the local copy has a pending local edit
 * (`syncedAt === null`) and a later `updatedAt`, keep local; otherwise take the
 * server copy.**
 *
 * Two consequences worth being explicit about, because both look surprising
 * until you hold the alternative up against the project's first rule — never
 * lose a trainee's data:
 *
 * - A local edit that is *older* than the server's loses. It was made against
 *   a copy the server has already moved past, and the outbox will have pushed
 *   it already or found it stale.
 * - Identical timestamps take the server copy. Two writes in the same
 *   millisecond means one device wrote it and the server echoed it back; there
 *   is nothing to lose.
 */

export type MergeDecision = 'take-server' | 'keep-local';

type Mergeable = Pick<LocalCase | LocalProcedure, 'updatedAt' | 'syncedAt'>;

export function mergeDecision(
  local: Mergeable | undefined,
  incoming: Pick<LocalCase | LocalProcedure, 'updatedAt'>,
): MergeDecision {
  // Nothing local: this is a record from another device, or a fresh install
  // restoring a logbook. Take it.
  if (local === undefined) return 'take-server';

  const hasPendingEdit = local.syncedAt === null;
  const localIsNewer = local.updatedAt.getTime() > incoming.updatedAt.getTime();

  return hasPendingEdit && localIsNewer ? 'keep-local' : 'take-server';
}

/**
 * Merging a whole pull, as a pure decision list. The caller does the writing,
 * so this can be tested without a database.
 */
export function mergePlan<T extends { id: string; updatedAt: Date }>(
  locals: ReadonlyMap<string, Mergeable>,
  incoming: readonly T[],
): { write: T[]; keep: string[] } {
  const write: T[] = [];
  const keep: string[] = [];

  for (const record of incoming) {
    if (mergeDecision(locals.get(record.id), record) === 'take-server') {
      write.push(record);
    } else {
      keep.push(record.id);
    }
  }

  return { write, keep };
}
